// src/components/exams/ExamManagement/ViewExam.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  PlusIcon,
  BookOpenIcon,
  AcademicCapIcon,
  PencilIcon,
  DocumentTextIcon,
  HomeIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { getExams } from "../../../services/Exams/ExamsService";

const ViewExam = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExam = async () => {
      if (!uuid) {
        setError('UUID не найден в URL');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const examData = await getExams({ params: { uuid } });
        setExam(examData);
        setError(null);
      } catch (err) {
        console.error('Ошибка при загрузке экзамена:', err);
        setError('Не удалось загрузить данные экзамена');
      } finally {
        setLoading(false);
      }
    };

    fetchExam();
  }, [uuid]);

  const getQuestionById = (id) => {
    return null;
  };

  const handleCreateTest = () => {
    navigate('/tests/create', { 
      state: { 
        examUuid: uuid,
        examName: exam?.name 
      }
    });
  };

  const handleEditTest = (testId) => {
    navigate(`/tests/${testId}/edit`, {
      state: {
        examUuid: uuid,
        examName: exam?.name
      }
    });
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Загрузка экзамена...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        {/* Навигационное меню */}
        <nav className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-6">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <HomeIcon className="h-5 w-5" />
              Главная
            </Link>
            <Link 
              to="/exams" 
              className="flex items-center gap-2 text-blue-600 font-medium"
            >
              <AcademicCapIcon className="h-5 w-5" />
              Экзамены
            </Link>
            <Link 
              to="/tests" 
              className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors"
            >
              <BookOpenIcon className="h-5 w-5" />
              Тесты
            </Link>
            <Link 
              to="/questions" 
              className="flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
              Вопросы
            </Link>
          </div>
        </nav>

        <button
          onClick={() => navigate('/exams')}
          className="text-blue-600 hover:text-blue-700 flex items-center gap-2 mb-6 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Назад к экзаменам
        </button>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Ошибка загрузки</div>
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="p-8">
        {/* Навигационное меню */}
        <nav className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-6">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <HomeIcon className="h-5 w-5" />
              Главная
            </Link>
            <Link 
              to="/exams" 
              className="flex items-center gap-2 text-blue-600 font-medium"
            >
              <AcademicCapIcon className="h-5 w-5" />
              Экзамены
            </Link>
            <Link 
              to="/tests" 
              className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors"
            >
              <BookOpenIcon className="h-5 w-5" />
              Тесты
            </Link>
            <Link 
              to="/questions" 
              className="flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
              Вопросы
            </Link>
          </div>
        </nav>

        <button
          onClick={() => navigate('/exams')}
          className="text-blue-600 hover:text-blue-700 flex items-center gap-2 mb-6 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Назад к экзаменам
        </button>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <div className="text-yellow-600 text-lg font-medium mb-2">Экзамен не найден</div>
          <p className="text-yellow-700">Экзамен с UUID "{uuid}" не существует</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Навигационное меню */}
      <nav className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-6">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
          >
            <HomeIcon className="h-5 w-5" />
            Главная
          </Link>
          <Link 
            to="/exams" 
            className="flex items-center gap-2 text-blue-600 font-medium"
          >
            <AcademicCapIcon className="h-5 w-5" />
            Экзамены
          </Link>
          <Link 
            to="/tests" 
            className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors"
          >
            <BookOpenIcon className="h-5 w-5" />
            Тесты
          </Link>
          <Link 
            to="/questions" 
            className="flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors"
          >
            <QuestionMarkCircleIcon className="h-5 w-5" />
            Вопросы
          </Link>
        </div>
      </nav>

      <button
        onClick={() => navigate('/exams')}
        className="text-blue-600 hover:text-blue-700 flex items-center gap-2 mb-6 transition-colors"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Назад к экзаменам
      </button>
      
      <div className="bg-white border border-gray-200 rounded-lg p-8">
        <div className="flex justify-between items-start mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center">
              <AcademicCapIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">{exam?.name}</h1>
              <p className="text-gray-600 mt-1">{exam?.description}</p>
              <div className="flex gap-4 mt-2 text-sm text-gray-500">
                <span>Код: {exam?.code}</span>
                <span>Длительность: {exam?.duration_minutes} минут</span>
                <span>Проходной балл: {exam?.passing_score}%</span>
              </div>
              <div className="flex gap-4 mt-1 text-sm text-gray-500">
                <span>Тестов: {exam?.tests_count}</span>
                <span>Общая длительность: {exam?.total_duration} минут</span>
                {exam?.category && <span>Категория: {exam?.category}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreateTest}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm font-medium"
            >
              <PlusIcon className="h-4 w-4" />
              Создать тест
            </button>
            <Link
              to="/tests"
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm font-medium"
            >
              <BookOpenIcon className="h-4 w-4" />
              Все тесты
            </Link>
          </div>
        </div>
        
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Тесты в экзамене</h2>
          
          {exam?.exam_tests?.map(test => (
            <div key={test.id} className="border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                    <BookOpenIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{test.name}</h3>
                    <p className="text-gray-600 text-sm">{test.description}</p>
                    <p className="text-gray-500 text-xs mt-1">Длительность: {test.duration_minutes} минут</p>
                  </div>
                </div>
                <button
                  onClick={() => handleEditTest(test.id)}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm flex items-center gap-1 transition-colors"
                >
                  <PencilIcon className="h-4 w-4" />
                  Редактировать
                </button>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Вопросы в тесте ({test.questions?.length || 0})</p>
                
                {test.questions?.length > 0 ? (
                  <div className="space-y-2">
                    {test.questions.slice(0, 3).map(questionId => {
                      const question = getQuestionById(questionId);
                      return question ? (
                        <div key={questionId} className="bg-white p-3 rounded border-l-4 border-blue-500">
                          <p className="text-sm text-gray-700">{question.text}</p>
                          <div className="flex gap-2 mt-1">
                            <span className="text-xs text-gray-500">{question.subject}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              question.difficulty === 'Легкий' ? 'bg-green-100 text-green-700' :
                              question.difficulty === 'Средний' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {question.difficulty}
                            </span>
                          </div>
                        </div>
                      ) : null;
                    })}
                    {test.questions.length > 3 && (
                      <p className="text-xs text-gray-500 text-center py-2">
                        ... и еще {test.questions.length - 3} вопросов
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <DocumentTextIcon className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500 text-sm">Вопросы не добавлены</p>
                    <Link
                      to="/questions/create"
                      className="text-purple-600 hover:text-purple-700 text-sm underline mt-1 inline-block"
                    >
                      Создать вопрос
                    </Link>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {(!exam?.exam_tests || exam.exam_tests.length === 0) && (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <BookOpenIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">В этом экзамене пока нет тестов</h3>
              <p className="text-gray-600 mb-4">Создайте первый тест для начала работы</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleCreateTest}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Создать тест
                </button>
                <Link
                  to="/questions"
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Банк вопросов
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewExam;